import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {FaCheckCircle, FaCloudUploadAlt, FaHistory, FaRedo, FaServer} from 'react-icons/fa';
import * as api from '../api';
import CommonMetaTag from './common-meta-tag';
import NvrExecutionFailure from './nvr-execution-failure';


const prettyJson = value => `${JSON.stringify(value, null, 2)}\n`;

const apiErrorText = response => {
  const error = response && response.json && response.json.error;
  if (error && typeof error === 'object') {
    return [error.path, error.code, error.message].filter(Boolean).join(' — ');
  }
  return error || (response && response.status === -1 ? 'network_error' : `HTTP ${response && response.status}`);
};

const onlineRecently = lastSeenAt => {
  const timestamp = Date.parse(lastSeenAt);
  return Number.isFinite(timestamp) && Date.now() - timestamp < 120000;
};

export const AdminNvrConfiguration = ({token}) => {
  const {t} = useTranslation();
  const [devices, setDevices] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [history, setHistory] = useState(null);
  const [editorText, setEditorText] = useState('');
  const [loadedRevision, setLoadedRevision] = useState(null);
  const [validatedText, setValidatedText] = useState(null);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [section, setSection] = useState('configuration');

  const selectedDevice = devices.find(device => device.deviceUid === selectedUid) || null;
  const secretSection = section === 'secrets';
  const managedState = selectedDevice && secretSection
    ? (selectedDevice.secrets || {desiredRevision: null, reported: {}, inSync: false})
    : selectedDevice && {
      desiredRevision: selectedDevice.desiredRevision,
      reported: selectedDevice.reported || {},
      inSync: selectedDevice.inSync
    };

  const loadDevices = async preferredUid => {
    const response = await api.adminListNvrDevices(token);
    if (response.status !== 200) {
      setError(apiErrorText(response));
      return null;
    }
    const items = response.json.devices || [];
    setDevices(items);
    const nextUid = preferredUid && items.some(item => item.deviceUid === preferredUid)
      ? preferredUid
      : (selectedUid && items.some(item => item.deviceUid === selectedUid)
        ? selectedUid
        : (items[0] && items[0].deviceUid));
    setSelectedUid(nextUid || null);
    return nextUid || null;
  };

  const loadRevision = async (deviceUid, revision) => {
    setBusy(true);
    setError(null);
    const response = secretSection
      ? await api.adminGetNvrSecretRevision(deviceUid, revision, token)
      : await api.adminGetNvrConfigurationRevision(deviceUid, revision, token);
    setBusy(false);
    if (response.status !== 200) {
      setError(apiErrorText(response));
      return;
    }
    setEditorText(prettyJson(secretSection ? response.json.secretsDocument : response.json.configuration));
    setLoadedRevision(response.json.revision);
    setValidatedText(null);
    setValidation(null);
  };

  const loadHistory = async (deviceUid, loadDocument = true) => {
    if (!deviceUid) {
      setHistory(null);
      setEditorText('');
      return;
    }
    setBusy(true);
    setError(null);
    const response = secretSection
      ? await api.adminListNvrSecretRevisions(deviceUid, token)
      : await api.adminListNvrConfigurationRevisions(deviceUid, token);
    setBusy(false);
    if (response.status !== 200) {
      setError(apiErrorText(response));
      return;
    }
    setHistory(response.json);
    if (loadDocument) {
      const revision = response.json.desiredRevision
        || (response.json.revisions[0] && response.json.revisions[0].revision);
      if (revision) {
        await loadRevision(deviceUid, revision);
      } else {
        setEditorText('');
        setLoadedRevision(null);
        setValidatedText(null);
        setValidation(null);
      }
    }
  };

  useEffect(() => {
    let active = true;
    api.adminListNvrDevices(token).then(response => {
      if (!active) {
        return;
      }
      if (response.status !== 200) {
        setError(apiErrorText(response));
        return;
      }
      const items = response.json.devices || [];
      setDevices(items);
      setSelectedUid(items[0] ? items[0].deviceUid : null);
    });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (selectedUid) {
      loadHistory(selectedUid);
    }
    // loadHistory intentionally follows the selected device only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUid, section]);

  const parsedEditor = () => {
    try {
      return {value: JSON.parse(editorText)};
    } catch (parseError) {
      return {error: `${t('NVR_CONFIG_INVALID_JSON')}: ${parseError.message}`};
    }
  };

  const validate = async () => {
    const parsed = parsedEditor();
    setNotice(null);
    if (parsed.error) {
      setValidation(null);
      setValidatedText(null);
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError(null);
    const response = secretSection
      ? await api.adminValidateNvrSecrets(selectedUid, parsed.value, token)
      : await api.adminValidateNvrConfiguration(selectedUid, parsed.value, token);
    setBusy(false);
    if (response.status !== 200) {
      setValidation(null);
      setValidatedText(null);
      setError(apiErrorText(response));
      return;
    }
    setValidatedText(editorText);
    setValidation(response.json);
    setNotice(t(secretSection ? 'NVR_SECRETS_VALIDATED' : 'NVR_CONFIG_VALIDATED', {revision: response.json.nextRevision}));
  };

  const publish = async () => {
    if (validatedText !== editorText || !window.confirm(t(secretSection ? 'NVR_SECRETS_CONFIRM_PUBLISH' : 'NVR_CONFIG_CONFIRM_PUBLISH'))) {
      return;
    }
    const parsed = parsedEditor();
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    const response = secretSection
      ? await api.adminPublishNvrSecrets(selectedUid, parsed.value, token)
      : await api.adminPublishNvrConfiguration(selectedUid, parsed.value, token);
    setBusy(false);
    if (response.status !== 201) {
      setError(apiErrorText(response));
      setValidatedText(null);
      return;
    }
    setNotice(t(secretSection ? 'NVR_SECRETS_PUBLISHED' : 'NVR_CONFIG_PUBLISHED', {revision: response.json.revision}));
    await loadDevices(selectedUid);
    await loadHistory(selectedUid);
  };

  const rollback = async revision => {
    if (!window.confirm(t(secretSection ? 'NVR_SECRETS_CONFIRM_ROLLBACK' : 'NVR_CONFIG_CONFIRM_ROLLBACK', {revision}))) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    const response = secretSection
      ? await api.adminRollbackNvrSecrets(selectedUid, revision, token)
      : await api.adminRollbackNvrConfiguration(selectedUid, revision, token);
    setBusy(false);
    if (response.status !== 200) {
      setError(apiErrorText(response));
      return;
    }
    setNotice(t(secretSection ? 'NVR_SECRETS_ROLLBACK_SENT' : 'NVR_CONFIG_ROLLBACK_SENT', {revision}));
    await loadDevices(selectedUid);
    await loadHistory(selectedUid);
  };

  const refresh = async () => {
    setNotice(null);
    setError(null);
    await loadDevices(selectedUid);
    await loadHistory(selectedUid, false);
  };

  return <main className="admin-nvr-config container-fluid">
    <CommonMetaTag/>
    <header className="admin-nvr-config__header">
      <div><span><FaServer/> {t('NVR_CONFIG_CONTROL_PLANE')}</span><h1>{t('NVR_CONFIG_TITLE')}</h1><p>{t('NVR_CONFIG_INTRO')}</p></div>
      <Button variant="outline-light" onClick={refresh} disabled={busy}><FaRedo/> {t('REFRESH')}</Button>
    </header>

    {error ? <Alert variant="danger" role="alert" dismissible onClose={() => setError(null)}>{error}</Alert> : null}
    {notice ? <Alert variant="success" role="status" dismissible onClose={() => setNotice(null)}>{notice}</Alert> : null}
    {busy ? <div className="admin-nvr-config__busy"><Spinner animation="border" size="sm"/> {t('LOADING')}</div> : null}

    <Row>
      <Col xl={3} lg={4}>
        <Card className="admin-nvr-config__devices">
          <Card.Header>{t('NVR_CONFIG_DEVICES')}</Card.Header>
          <Card.Body>
            {!devices.length ? <p>{t('NVR_CONFIG_NO_DEVICES')}</p> : devices.map(device => <button
              type="button"
              key={device.deviceUid}
              className={`admin-nvr-config__device ${device.deviceUid === selectedUid ? 'is-selected' : ''}`}
              onClick={() => setSelectedUid(device.deviceUid)}
            >
              <span><strong>{device.displayName}</strong><small>{device.deviceUid}</small></span>
              <span>
                <Badge variant={onlineRecently(device.lastSeenAt) ? 'success' : 'secondary'}>{onlineRecently(device.lastSeenAt) ? t('ONLINE') : t('OFFLINE')}</Badge>
                <Badge variant={device.inSync ? 'success' : 'warning'}>{device.inSync ? t('NVR_CONFIG_SYNCED') : t('NVR_CONFIG_NOT_SYNCED')}</Badge>
              </span>
            </button>)}
          </Card.Body>
        </Card>
      </Col>

      <Col xl={9} lg={8}>
        {selectedDevice ? <>
          <div className="admin-nvr-config__actions mb-3" role="tablist">
            <Button
              role="tab"
              aria-selected={!secretSection}
              variant={!secretSection ? 'primary' : 'outline-primary'}
              onClick={() => setSection('configuration')}
            >{t('NVR_CONFIG_TAB_CONFIGURATION')}</Button>
            <Button
              role="tab"
              aria-selected={secretSection}
              variant={secretSection ? 'primary' : 'outline-primary'}
              onClick={() => setSection('secrets')}
            >{t('NVR_CONFIG_TAB_SECRETS')}</Button>
          </div>
          <section className="admin-nvr-config__summary">
            <div><span>{t('NVR_CONFIG_DEVICE')}</span><strong>{selectedDevice.displayName}</strong><small>{selectedDevice.deviceUid}</small></div>
            <div><span>{t('NVR_CONFIG_TRANSPORT')}</span><strong>{selectedDevice.transportMode}</strong><small>{(selectedDevice.fieldIds || []).join(', ') || '—'}</small></div>
            <div><span>{t('NVR_CONFIG_DESIRED')}</span><strong>{managedState.desiredRevision || '—'}</strong><small>{t('NVR_CONFIG_REVISION')}</small></div>
            <div><span>{t('NVR_CONFIG_APPLIED')}</span><strong>{managedState.reported.revision || '—'}</strong><small>{managedState.reported.state || 'UNKNOWN'}</small></div>
          </section>

          {managedState.reported.reason ? <Alert variant="warning">{JSON.stringify(managedState.reported.reason)}</Alert> : null}
          <NvrExecutionFailure failure={selectedDevice.lastCommandError} timestamp={selectedDevice.lastCommandErrorAt}/>
          {secretSection ? <Alert variant="warning">{t('NVR_SECRETS_ADMIN_WARNING')}</Alert> : null}
          <Card className="admin-nvr-config__editor-card">
            <Card.Header>
              <span>{t('NVR_CONFIG_EDITOR')} {loadedRevision ? `— ${t('NVR_CONFIG_REVISION')} ${loadedRevision}` : ''}</span>
              <small>{t(secretSection ? 'NVR_SECRETS_EDITOR_NOTICE' : 'NVR_CONFIG_SECRETS_NOTICE')}</small>
            </Card.Header>
            <Card.Body>
              {!editorText ? <Alert variant="info">{t('NVR_CONFIG_EMPTY_HELP')}</Alert> : null}
              <Form.Group controlId="nvr-configuration-json">
                <Form.Label>{t(secretSection ? 'NVR_SECRETS_JSON_LABEL' : 'NVR_CONFIG_JSON_LABEL')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={24}
                  value={editorText}
                  spellCheck={false}
                  onChange={event => {
                    setEditorText(event.target.value);
                    setValidatedText(null);
                    setValidation(null);
                    setNotice(null);
                  }}
                />
              </Form.Group>
              <div className="admin-nvr-config__actions">
                <Button variant="outline-primary" onClick={validate} disabled={busy || !editorText}>{t('NVR_CONFIG_VALIDATE')}</Button>
                <Button variant="primary" onClick={publish} disabled={busy || validatedText !== editorText}>
                  <FaCloudUploadAlt/> {t(secretSection ? 'NVR_SECRETS_PUBLISH' : 'NVR_CONFIG_PUBLISH')}
                </Button>
                {validation ? <span className="admin-nvr-config__validation"><FaCheckCircle/> {t('NVR_CONFIG_READY_FOR_REVISION', {revision: validation.nextRevision})}</span> : null}
              </div>
            </Card.Body>
          </Card>

          <Card className="admin-nvr-config__history">
            <Card.Header><FaHistory/> {t('NVR_CONFIG_HISTORY')}</Card.Header>
            <div
              className="admin-nvr-config__history-scroll"
              role="region"
              aria-label={t('NVR_CONFIG_HISTORY')}
              tabIndex="0"
            >
              <Table hover>
                <thead><tr><th>{t('NVR_CONFIG_REVISION')}</th><th>{t('STATE')}</th><th>{t('DATE')}</th><th>SHA-256</th><th>{t('ACTIONS')}</th></tr></thead>
                <tbody>
                  {history && history.revisions.map(item => <tr key={item.revision}>
                    <td>{item.revision}</td>
                    <td>{item.desired ? <Badge variant="primary">{t('NVR_CONFIG_DESIRED')}</Badge> : '—'}</td>
                    <td>{new Date(item.createdAt).toLocaleString()}</td>
                    <td><code title={item.sha256}>{item.sha256.slice(0, 12)}…</code></td>
                    <td>
                      <Button size="sm" variant="outline-secondary" onClick={() => loadRevision(selectedUid, item.revision)}>{t('NVR_CONFIG_LOAD')}</Button>{' '}
                      <Button size="sm" variant="outline-warning" disabled={item.desired || busy} onClick={() => rollback(item.revision)}>{t('NVR_CONFIG_ROLLBACK')}</Button>
                    </td>
                  </tr>)}
                </tbody>
              </Table>
            </div>
          </Card>
        </> : null}
      </Col>
    </Row>
  </main>;
};

const mapStateToProps = ({login: {token}}) => ({token});

export default withRouter(connect(mapStateToProps)(AdminNvrConfiguration));
